import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/css';
import OrganizationContainer from '../../../state/containers/OrganizationContainer';
import OrganizationModel from '../../../models/organizations/api/OrganizationModel';
import SearchField from '../../../components/forms/SearchField';
import Loader from '../../../components/general/Loader';
import ModalHeader from '../../../components/general/ModalHeader';
import SmallModalLayout from '../../../components/structure/SmallModalLayout';
import { color_shades_dark, color_shades_darker, color_shades_lighter, ocean_blue } from '../../../constants/colors';

interface DisabledOrganizationSearchModalProps {
    isVisible: boolean
    onClose: () => void
    onOrganizationSelected: (organization: OrganizationModel) => Promise<void>
    organizationContainer: OrganizationContainer
}

const DisabledOrganizationSearchModal: React.FC<DisabledOrganizationSearchModalProps> = (props) => {
    const [search, setSearch] = useState('');

    useEffect(() => {
        if (!props.isVisible) return;

        const normalizedSearch = search.trim();
        if (normalizedSearch.length < 2) {
            props.organizationContainer.clearDisabledOrganizationSearch();
            return;
        }

        const timeout = window.setTimeout(() => {
            props.organizationContainer.searchDisabledOrganizations(normalizedSearch);
        }, 300);

        return () => window.clearTimeout(timeout);
    }, [search, props.isVisible]);

    const handleClose = () => {
        setSearch('');
        props.organizationContainer.clearDisabledOrganizationSearch();
        props.onClose();
    };

    const handleOrganizationSelected = async (organization: OrganizationModel) => {
        await props.onOrganizationSelected(organization);
        handleClose();
    };

    const normalizedSearch = search.trim();
    const results = props.organizationContainer.state.disabledOrganizationSearchResults;
    const isLoading = props.organizationContainer.state.isLoadingDisabledOrganizations;
    const errors = props.organizationContainer.state.errors;

    return (
        <SmallModalLayout
            isVisible={props.isVisible}
            onClose={handleClose}
            className={modalStyle}
            innerClassName={modalInnerStyle}
        >
            <ModalHeader title="Find a Disabled Organization" onClose={handleClose} />
            <ModalBody>
                <SearchField
                    name="disabledOrganizationSearch"
                    placeholder="Search disabled organizations"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    boxType="square"
                    handleClear={() => setSearch('')}
                />
                {normalizedSearch.length < 2 && (
                    <StatusMessage>Enter at least two characters to search.</StatusMessage>
                )}
                {normalizedSearch.length >= 2 && isLoading && <LoaderWrapper><Loader /></LoaderWrapper>}
                {normalizedSearch.length >= 2 && !isLoading && errors.length > 0 && (
                    <ErrorMessage>{errors.join(' ')}</ErrorMessage>
                )}
                {normalizedSearch.length >= 2 && !isLoading && errors.length === 0 && results.length === 0 && (
                    <StatusMessage>No disabled organizations matched that search.</StatusMessage>
                )}
                {normalizedSearch.length >= 2 && !isLoading && results.length > 0 && (
                    <ResultsList>
                        {results.map(organization => (
                            <ResultButton
                                key={organization.id}
                                type="button"
                                onClick={() => handleOrganizationSelected(organization)}
                            >
                                <span>{organization.name}</span>
                                <small>Disabled</small>
                            </ResultButton>
                        ))}
                    </ResultsList>
                )}
            </ModalBody>
        </SmallModalLayout>
    );
};

const modalStyle = css`
    width: 580px;
    min-height: 420px;
`;

const modalInnerStyle = css`
    display: flex;
    flex-direction: column;
    min-height: 420px;
`;

const ModalBody = styled.div`
    display: flex;
    flex: 1;
    flex-direction: column;
    padding: 24px 32px 32px;
    overflow-y: auto;
`;

const ResultsList = styled.div`
    border: 1px solid ${color_shades_dark};
    border-radius: 8px;
    overflow: hidden;
`;

const ResultButton = styled.button`
    align-items: center;
    background: white;
    border: 0;
    border-bottom: 1px solid ${color_shades_dark};
    color: ${color_shades_darker};
    cursor: pointer;
    display: flex;
    font-family: Muli;
    font-size: 14px;
    justify-content: space-between;
    padding: 16px;
    text-align: left;
    width: 100%;

    &:last-child {
        border-bottom: 0;
    }

    &:hover {
        background: ${color_shades_lighter};
        color: ${ocean_blue};
    }

    small {
        color: ${color_shades_darker};
        font-size: 12px;
    }
`;

const StatusMessage = styled.p`
    color: ${color_shades_darker};
    font-family: Muli;
    font-size: 14px;
    margin: 24px 0;
    text-align: center;
`;

const ErrorMessage = styled(StatusMessage)`
    color: #bd251a;
`;

const LoaderWrapper = styled.div`
    min-height: 160px;
    position: relative;
`;

export default DisabledOrganizationSearchModal;
